import { useId } from "@floating-ui/react";
import { Locale } from "@omni/types/enums/locale.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { values } from "@omni/utils/object.utils";
import { cn } from "@omni/utils/styles";
import Select, { components } from "react-select";

import { useReviewsContext } from "@/components/Product/Reviews/context/reviews.context";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type ReviewLocaleSelectOption = {
    value: Locale;
    label: string;
};

type Props = {
    className?: string;
};

export const Locales = ({ className }: Props) => {
    const { t } = useGranite();

    const { locales, toggleLocale } = useReviewsContext();

    const toOption = (value: Locale) => ({
        value: value,
        label: t(`product.reviews.filters.locale.${value.toString()}`)
    });

    const options = values(Locale).map(toOption);

    const isSelected = (locale?: Locale) => {
        if (!isDefined(locale)) return false;

        return locales.includes(locale);
    };

    const handleOnLocaleChange = (option: ReviewLocaleSelectOption | null) => {
        if (!isDefined(option)) return;

        toggleLocale(option.value);
    };

    return (
        <Select
            className={cn(className)}
            openMenuOnFocus={true}
            options={options}
            isSearchable={false}
            blurInputOnSelect={true}
            value={null}
            name="locales"
            id="locales"
            instanceId={useId()}
            onChange={handleOnLocaleChange}
            classNames={{
                menu: () => "overflow-hidden",
                menuList: () => "p-0"
            }}
            components={{
                Placeholder: (props) => (
                    <components.Placeholder {...props}>
                        <div style={{ color: "#000" }}>
                            {t("product.reviews.filters.locale.title")}
                        </div>
                    </components.Placeholder>
                ),
                SingleValue: (props) => (
                    <components.SingleValue {...props}>
                        {t("product.reviews.filters.locale.title")}
                    </components.SingleValue>
                ),
                Option: (props) => {
                    const selected = isSelected(props.data?.value);

                    return (
                        <components.Option
                            {...props}
                            className={cn(
                                "tw:flex tw:items-center",
                                {
                                    "tw:border-l-3 tw:border-primary tw:not-hover:bg-grey!":
                                        selected
                                },
                                props.className
                            )}
                        >
                            <span className={cn("tw:mr-2")}>
                                <i
                                    className={cn(
                                        "las",
                                        selected && "la-check-circle",
                                        !selected && "la-plus-circle"
                                    )}
                                />
                            </span>

                            <span>{props.children}</span>
                        </components.Option>
                    );
                }
            }}
        />
    );
};
